import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getProjects, getProject } from '../../../apis/project-builder';
import { useTranslationContext } from "./project-builder-main.jsx";
import { useAddProduct, useAddAttachment, useUpdateProduct } from '../../../components/project-builder-custom-hooks';
import Popover from '@mui/material/Popover';
import Select from 'react-select';
import { postGetTranslations } from '../../../../shared/apis/translations';
import toast from '../../../../shared/components/toast';

export default function QuickAdd({ projectId, item, index, videoNodeId, videoTitle, buttonText }) {

    const projectBuilderSearch = document.querySelector('.project-builder-search');

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'quick-add' : undefined;

    const [fetchProjectDetail, setFetchProjectDetail] = useState(false);
    const [addToProjectId, setAddToProjectId] = useState(projectId);

    const { data, isSuccess } = useQuery(['projects-list'], getProjects);

    const { data: projectData, isSuccess: projectIsSuccess } = useQuery({
        queryKey: ['project-detail', addToProjectId],
        queryFn: () => getProject(addToProjectId),
        enabled: fetchProjectDetail
    });

    const [isAddingItem, setIsAddingItem] = useState(false);

    const [quantity, setQuantity] = useState(1);

    const { mutate: addProduct, isLoading } = useAddProduct(addToProjectId);
    const { mutate: addAttachment, isLoading: attachmentIsLoading } = useAddAttachment(addToProjectId);
    const { mutate: updateProduct, isLoading: updateIsLoading } = useUpdateProduct(addToProjectId);

    const [projectOptions, setProjectOptions] = useState([]);

    const isMounted = useRef(false);
    const translationContext = useTranslationContext()?.translations;
    const [translationsFallback, setTranslationsFallback] = useState({});
    const translations = translationContext ?? translationsFallback; // For when we're not inside project builder

    const [newDiscount, setNewDiscount] = useState({
        //id: videoNodeId ? "" : item.Id,
        discountType: "None",
        discountValue: 0
    });

    const [percentageAmount, setPercentageAmount] = useState(0);
    const [dollarAmount, setDollarAmount] = useState(0);

    useEffect(() => {
        if (translationContext) return;

        isMounted.current = true;

        let translationKeys = {
            VideoAddedMsg: 'ProjectBuilder.Label.VideoAddedMsg',
            QuickAdd: 'ProjectBuilder.Label.QuickAdd',
            BtnAddToProjectBuilder: 'ProjectBuilder.Label.BtnAddToProjectBuilder',
            SeeDetails: 'ProjectBuilder.Label.SeeDetails',
            BtnAddToProject: 'ProjectBuilder.Label.BtnAddToProject',
            SelectProject: 'ProjectBuilder.Label.SelectProject',
            Discount: 'ProjectBuilder.Label.Discount',
            BtnAddToProject: 'ProjectBuilder.Label.BtnAddToProject',
            None: 'Global.Label.None',
            FiftyTenDiscount: 'ProjectBuilder.Label.FiftyTenDiscount',
            FiftyTenOpenDiscount: 'ProjectBuilder.Label.FiftyTenOpenDiscount',
            FiftyTenOpenAmount: 'ProjectBuilder.Label.FiftyTenOpenAmount',
            CustomPercentageDiscountLabel: 'ProjectBuilder.Label.CustomPercentageDiscountLabel',
            PercentageAmount: 'ProjectBuilder.Label.PercentageAmount',
            CustomDollarAmount: 'ProjectBuilder.Label.CustomDollarAmount',
            DollarAmount: 'ProjectBuilder.Label.DollarAmount',
        };

        postGetTranslations(Object.values(translationKeys))
            .then(data => {
                const lookup = {};
                Object.keys(translationKeys).forEach(key => {
                    lookup[key] = data[translationKeys[key]];
                });

                if (isMounted.current) {
                    // Component sometimes gets unmounted before this finishes for some reason, which causes a memory leak
                    setTranslationsFallback(lookup);
                }
            });

        return () => isMounted.current = false;
    }, [translationContext]);

    const increaseQuantity = () => {
        setQuantity(prevState => prevState + 1);
    }

    const decreaseQuantity = () => {
        setQuantity(prevState => prevState - 1);
    }

    const handleSelectProject = (value) => {
        setAddToProjectId(value.value);
        setFetchProjectDetail(true);
    };

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAddItem = () => {
        setIsAddingItem(true); 

        if (projectIsSuccess) {

            let currentProductNodeId = item.nodeId || item.NodeId;
            const existingProduct = projectData.products.find(product => product.productNodeId === parseInt(currentProductNodeId));

            let productData = {
                productNodeId: currentProductNodeId,
                quantity: quantity,
                discountType: newDiscount.discountType,
                discountValue: newDiscount.discountValue
            }

            if (existingProduct) {
                productData = {
                    id: existingProduct.id,
                    quantity: existingProduct.quantity + quantity,
                    discountType: newDiscount.discountType,
                    discountValue: newDiscount.discountValue
                }

                updateProduct(productData, {
                    onSuccess: () => {
                        handleClose();
                        setIsAddingItem(false);
                    },
                    onError: () => {
                        toast.error("Error updating product");
                        setIsAddingItem(false);
                    }
                });
            }
            else {
                addProduct(productData, {
                    onSuccess: () => {
                        handleClose();
                        setIsAddingItem(false);
                    },
                    onError: () => {
                        toast.error("Error adding product");
                        setIsAddingItem(false);
                    }
                });
            }
        }
    }

    useEffect(() => {
        if (isSuccess) {
            let newOptions = [];
            data.forEach((project) => {
                let id = project.id;
                let name = project.name;
                let productCount = project.products.length;
                newOptions.push({
                    value: id,
                    label: name + " (" + productCount + ")"
                });
            });
            setProjectOptions(newOptions);
        }
    }, [data])

    return (
        <>
            {isSuccess ?
                <>
                    <button
                        className="btn btn--quickAdd btn-outline-alt"
                        aria-describedby={id}
                        onClick={handleClick}
                        type="button"
                    >
                        {projectBuilderSearch ? translations.QuickAdd : translations.BtnAddToProjectBuilder}
                    </button>

                    <Popover
                        className="quick-add"
                        role="presentation"
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: ((index + 1) % 2 == 0) ? 'left' : 'right'
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: ((index + 1) % 2 == 0) ? 'right' : 'left'
                        }}
                    >
                        <div className="project-builder">
                            <div className="popper quick-add rounded-border">
                                <button className="project-builder-modal-close" onClick={handleClose}>
                                    <svg id="header-ask-close-svg" className="icon icon-close"><use xlinkHref="#icon-close"></use></svg>
                                </button>
                                <div className="product-number">{item?.itemNumber}</div>
                                <div className="product-title">{item ? item.productName : videoTitle}</div>
                                <div className="product-details-container">
                                    {item ?
                                        <>
                                            <a href={item?.pageUrl}>{translations.SeeDetails}</a>
                                            <div className="ml-auto quick-add-quantity">
                                                <span className="quantity--decrease" onClick={decreaseQuantity}><img src="/img/icon-decrease.png" alt="decrease quantity" /></span>
                                                <span>{quantity}</span>
                                                <span className="quantity--increase" onClick={increaseQuantity}><img src="/img/icon-increase.png" alt="increase quantity" /></span>
                                            </div>
                                        </>
                                        : null
                                    }
                                </div>

                                <label>{translations.SelectProject}</label>
                                <Select
                                    classNamePrefix="styled-select"
                                    options={projectOptions}
                                    value={projectOptions.find(({ value }) => value === addToProjectId)}
                                    onChange={handleSelectProject}
                                />

                                <button type="button" className="btn btn-block no-arrow mt-1" onClick={handleAddItem} disabled={!projectIsSuccess || isAddingItem}>{translations.BtnAddToProject}</button>
                            </div>
                        </div>
                    </Popover>
                </>
                : null}
        </>
    );
}